import { useEffect, useState } from 'react'
import {
  trackCollapsedPaintedDoorViewed,
  trackCollapsedPaintedDoorV2Viewed,
  trackI18nPricingT3Viewed,
} from '@mc/checkout'
import { Conditional, ShowIf } from '@mc/conditional-rendering'
import { useFreeTrial } from '@mc/free-trial'
import { Trans } from '@mc/i18n'
import { usePromotion } from '@mc/promo-utils'
import { useStaticData } from '@mc/static-data-react'
import { useIsVisible } from '@mc/visibility'
import { useLanguageCleanupExpPricingText } from 'experiments/pen-5840_v1_core_membership_language_cleanup'
import { StaticData } from 'types/libs/staticData'
import { useMoneyBackPricingMicrocopy } from './useMoneyBackPricingMicrocopy'

export type PricingTextProps = {
  translationKey?:
    | 'long'
    | 'short'
    | 'shortPriceBold'
    | 'shortPriceStyled'
    | 'longWithSeries'
    | 'pen-5613_v2-cm-innovative-swing' // pen-5613_v2 cm_innovative_swing experiment
    | 'pen-5646_v1-sticky-footer'
  children?: React.ReactNode
  className?: string
  isStickyFooter?: boolean
}

const config = {
  threshold: 0.7,
  duration: { visible: 500 },
}

export const PricingText = ({
  translationKey = 'long',
  children,
  className,
  isStickyFooter = false,
}: PricingTextProps) => {
  const { annualPass } = useStaticData<StaticData>()
  const { trial } = useFreeTrial()
  const { isPromotionOn } = usePromotion()
  const isMoneyBackPricingPage = useMoneyBackPricingMicrocopy()
  const { isLanguageCleanupPricingText, instructors } =
    useLanguageCleanupExpPricingText()

  const { ref } = useTrackingEntryPaintedDoorViewed()

  if (!annualPass) {
    return null
  }

  const price = annualPass.monetizedYearPricePerMonth
  const discountedPrice = annualPass.monetizedYearPricePerMonthDiscounted
  const hasDiscountedPrice = isPromotionOn && !!discountedPrice
  const showMoneyBackPricing = isMoneyBackPricingPage && !isStickyFooter

  const promoKey =
    translationKey === 'shortPriceStyled'
      ? 'styled.pricingTextPromo'
      : `${isLanguageCleanupPricingText ? 'pen-5840_v1.' : ''}pricingTextPromo`

  const defaultPricingText = () => {
    if (!showMoneyBackPricing) {
      return (
        <Trans
          i18nKey={`common.pricing.${isLanguageCleanupPricingText ? 'pen-5840_v1.' : ''}${translationKey}`}
          values={{ price, instructors }} // instructors is only used in the pen-5840_v1 experiment
          ns='@mc/next'
        >
          {children}
        </Trans>
      )
    }

    if (translationKey === 'shortPriceStyled') {
      return (
        <Trans
          i18nKey='common.pricing.styled.pricingText'
          values={{ price }}
          ns='@mc/next'
        >
          {children}
        </Trans>
      )
    }

    return (
      <Trans
        ns='@mc/next'
        i18nKey={`common.pricing.${isLanguageCleanupPricingText ? 'pen-5840_v1.' : ''}pricingText`}
        values={{ price, instructors }} // instructors is only used in the pen-5840_v1 experiment
      />
    )
  }

  return (
    <span className={className} ref={ref}>
      <Conditional>
        <ShowIf
          condition={translationKey === 'pen-5613_v2-cm-innovative-swing'}
        >
          <Trans
            ns='@mc/next'
            i18nKey={`common.pricing.${translationKey}.${hasDiscountedPrice ? 'discount' : 'regular'}`}
            values={{
              price,
              newPrice: discountedPrice,
            }}
          >
            <span className='mc-text--strike' />
            <span className='mc-text--bold' />
          </Trans>
        </ShowIf>

        <ShowIf condition={trial.eligible}>
          <Trans
            ns='@mc/next'
            i18nKey='trial.pricing'
            values={{ trialType: trial.strings.type }}
          />
        </ShowIf>

        <ShowIf condition={hasDiscountedPrice}>
          <Trans
            i18nKey={
              showMoneyBackPricing
                ? `common.pricing.${promoKey}`
                : `common.pricing.${isLanguageCleanupPricingText ? 'pen-5840_v1.' : ''}discount.${translationKey}`
            }
            values={{
              strikeThroughPrice: annualPass.monetizedYearPricePerMonth,
              price: discountedPrice,
              instructors, // pen-5840_v1 experiment
            }}
            ns='@mc/next'
          >
            <span className='mc-opacity--hinted mc-text--bold' />
            <span className='mc-opacity--hinted mc-text--strike' />
            <span className='mc-text--bold' />
          </Trans>
        </ShowIf>

        <ShowIf condition={isPromotionOn && !discountedPrice}>
          {defaultPricingText()}
        </ShowIf>

        <ShowIf isDefault>{defaultPricingText()}</ShowIf>
      </Conditional>
    </span>
  )
}

const useTrackingEntryPaintedDoorViewed = () => {
  // prcng-107_v1 experiment
  // using the TrackingContainer is not possible here since this component
  // is being used inside <p> tags what triggers a react-hydration-error.
  // Workaround: replicate TrackingContainer behavior
  const { isVisible, ref } = useIsVisible<HTMLSpanElement>(config)
  const [hasTrackedView, setHasTrackedView] = useState(false)
  useEffect(() => {
    if (isVisible && !hasTrackedView) {
      setHasTrackedView(true)
      trackCollapsedPaintedDoorViewed()
      trackCollapsedPaintedDoorV2Viewed()
      trackI18nPricingT3Viewed()
    }
  }, [isVisible, hasTrackedView])

  return { ref }
}
